import { useState } from "react";
import { toast } from "sonner";
import { Buildings, Copy, ArrowSquareOut, WarningCircle } from "@phosphor-icons/react";
import { copyToClipboard } from "@/lib/csv";

const shortHost = (u) => {
    try {
        return new URL(u).hostname.replace(/^www\./, "");
    } catch {
        return u;
    }
};

export const CompetitorComparisonPanel = ({ pages = [], comparison, adhocPages = [] }) => {
    const [expanded, setExpanded] = useState(true);
    const allPages = [...pages, ...adhocPages];
    if (allPages.length === 0) return null;

    const goodPages = allPages.filter((p) => p?.keywords?.length && !p.error);
    const commonKeywords = comparison?.common_keywords || [];
    const uniqueByUrl = comparison?.unique_keywords || {};

    const handleCopyKeywords = async (kws, label) => {
        const ok = await copyToClipboard((kws || []).join("\n"));
        if (ok) toast.success(`Copied ${kws.length} ${label}`);
    };

    return (
        <section
            data-testid="competitor-comparison"
            className="border-2 border-ink bg-white"
        >
            <button
                onClick={() => setExpanded((v) => !v)}
                className="w-full flex items-center justify-between gap-3 px-5 py-4 border-b-2 border-ink bg-neutral-50 hover:bg-neutral-100 transition"
            >
                <div className="flex items-center gap-2">
                    <Buildings size={18} weight="bold" />
                    <h2 className="font-heading text-lg font-black uppercase tracking-tight">
                        Competitor Comparison
                    </h2>
                    <span className="mono-label ml-2">
                        {goodPages.length} of {allPages.length} pages analyzed
                    </span>
                </div>
                <span className={`transition-transform text-neutral-500 ${expanded ? "rotate-90" : ""}`}>▸</span>
            </button>

            {expanded && (
                <div className="p-5 space-y-6">
                    {/* Positioning diff */}
                    {comparison?.positioning_diff && (
                        <div>
                            <div className="mono-label mb-1.5">Positioning Contrast</div>
                            <p
                                data-testid="cc-positioning-diff"
                                className="text-sm leading-relaxed text-neutral-800"
                            >
                                {comparison.positioning_diff}
                            </p>
                        </div>
                    )}

                    {/* Common keywords */}
                    {commonKeywords.length > 0 && (
                        <div>
                            <div className="flex items-center justify-between mb-2">
                                <div className="mono-label">
                                    High-Priority · Common Across All ({commonKeywords.length})
                                </div>
                                <button
                                    data-testid="cc-copy-common"
                                    onClick={() => handleCopyKeywords(commonKeywords, "common keywords")}
                                    className="mono-label underline hover:no-underline flex items-center gap-1"
                                >
                                    <Copy size={11} weight="bold" />
                                    Copy all
                                </button>
                            </div>
                            <div
                                data-testid="cc-common-keywords"
                                className="flex flex-wrap gap-1.5"
                            >
                                {commonKeywords.map((k) => (
                                    <span
                                        key={k}
                                        className="border-2 border-klein bg-klein/5 text-klein px-2 py-0.5 font-mono text-xs font-bold"
                                    >
                                        {k}
                                    </span>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Per-page: unique keywords + positioning */}
                    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                        {allPages.map((p, idx) => {
                            const unique = uniqueByUrl[p.url] || [];
                            const host = shortHost(p.url);
                            return (
                                <div
                                    key={`${p.url}-${idx}`}
                                    data-testid={`cc-page-${idx}`}
                                    className="border border-ink bg-white p-4 space-y-2"
                                >
                                    <div className="flex items-start justify-between gap-2">
                                        <a
                                            href={p.url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="mono-label text-klein hover:underline inline-flex items-center gap-1 min-w-0"
                                        >
                                            <span className="truncate">{host}</span>
                                            <ArrowSquareOut size={11} weight="bold" className="shrink-0" />
                                        </a>
                                        {p.error && (
                                            <WarningCircle
                                                size={14}
                                                weight="bold"
                                                className="text-red-600 shrink-0"
                                                title={p.error}
                                            />
                                        )}
                                    </div>

                                    {p.error ? (
                                        <p className="text-xs text-red-600 font-mono">{p.error}</p>
                                    ) : (
                                        <>
                                            {p.positioning && (
                                                <p className="text-xs leading-relaxed text-neutral-700">
                                                    {p.positioning}
                                                </p>
                                            )}
                                            {p.angles?.length > 0 && (
                                                <div className="flex flex-wrap gap-1 pt-1">
                                                    {p.angles.slice(0, 5).map((a) => (
                                                        <span
                                                            key={a}
                                                            className="border border-neutral-400 bg-neutral-50 px-1.5 py-0.5 font-mono text-[10px] text-ink"
                                                        >
                                                            {a}
                                                        </span>
                                                    ))}
                                                </div>
                                            )}
                                            {unique.length > 0 ? (
                                                <div className="pt-1">
                                                    <div className="mono-label mb-1">Gap-Opportunity ({unique.length})</div>
                                                    <div className="flex flex-wrap gap-1">
                                                        {unique.map((k) => (
                                                            <button
                                                                key={k}
                                                                onClick={async () => {
                                                                    const ok = await copyToClipboard(k);
                                                                    if (ok) toast.success(`Copied "${k}"`);
                                                                }}
                                                                className="border border-neutral-300 bg-white hover:border-ink hover:bg-ink hover:text-white transition-colors px-1.5 py-0.5 font-mono text-[11px] text-ink"
                                                            >
                                                                {k}
                                                            </button>
                                                        ))}
                                                    </div>
                                                </div>
                                            ) : (
                                                p.keywords?.length > 0 &&
                                                comparison && (
                                                    <p className="mono-label text-neutral-500">
                                                        No unique keywords vs. other pages
                                                    </p>
                                                )
                                            )}
                                            {/* When there's no formal comparison (e.g. single ad-hoc scrape), just show the keywords list */}
                                            {!comparison && p.keywords?.length > 0 && (
                                                <div className="pt-1">
                                                    <div className="flex items-center justify-between mb-1">
                                                        <div className="mono-label">Keywords ({p.keywords.length})</div>
                                                        <button
                                                            onClick={() => handleCopyKeywords(p.keywords, "keywords")}
                                                            className="mono-label underline hover:no-underline"
                                                        >
                                                            Copy all
                                                        </button>
                                                    </div>
                                                    <div className="flex flex-wrap gap-1">
                                                        {p.keywords.map((k) => (
                                                            <span
                                                                key={k}
                                                                className="border border-neutral-300 bg-white px-1.5 py-0.5 font-mono text-[11px] text-ink"
                                                            >
                                                                {k}
                                                            </span>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}
                                        </>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}
        </section>
    );
};
